import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';

// URL -> sayfa adı
function getPageName(pathname) {
  if (pathname === '/dash' || pathname === '/dash/') return 'dashboard';
  if (pathname === '/login') return 'login';
  if (pathname === '/teklif-olustur' || pathname === '/dash/sepetim') return 'teklif_olustur';
  if (pathname.startsWith('/dash/')) {
    const view = pathname.replace('/dash/', '').split('/')[0];
    return view;
  }
  return pathname.replace(/^\//, '').replace(/\//g, '_') || 'home';
}

export default function FloatingHelpButton() {
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tourSteps, setTourSteps] = useState([]);
  const [tourLoading, setTourLoading] = useState(false);
  const menuRef = useRef(null);
  const driverRef = useRef(null);
  const pageName = getPageName(location.pathname);

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Sayfa değişince menüyü kapat
  useEffect(() => { setOpen(false); }, [location.pathname]);

  // Tur adımlarını yükle (rol filtresi olmadan - tüm adımları çek)
  const fetchTourSteps = useCallback(async () => {
    setTourLoading(true);
    try {
      const resp = await fetch(`/api/tour-steps/${pageName}`);
      if (resp.ok) {
        const data = await resp.json();
        setTourSteps(data.steps || []);
      }
    } catch (e) {}
    setTourLoading(false);
  }, [pageName]);

  // Sayfa değişince tur adımlarını yeniden yükle
  useEffect(() => {
    if (user) fetchTourSteps();
  }, [fetchTourSteps, user]);

  // İlk girişte otomatik tur başlat
  useEffect(() => {
    if (!user || tourSteps.length === 0) return;
    const key = `tour_completed_${pageName}`;
    if (localStorage.getItem(key) === 'true') return;
    const timer = setTimeout(() => {
      startTour();
      localStorage.setItem(key, 'true');
    }, 1500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tourSteps, pageName, user]);

  // Turu başlat
  const startTour = () => {
    if (tourSteps.length === 0) return;
    setOpen(false);

    if (driverRef.current) try { driverRef.current.destroy(); } catch (e) {}

    const driverSteps = tourSteps
      .filter(s => !s.target_selector || document.querySelector(s.target_selector))
      .map(s => ({
        element: s.target_selector || undefined,
        popover: { title: s.title, description: s.description, side: s.position || 'bottom', align: 'start' }
      }));

    if (driverSteps.length === 0) return;

    const d = driver({
      showProgress: true,
      showButtons: ['next', 'previous', 'close'],
      nextBtnText: 'Sonraki',
      prevBtnText: 'Önceki',
      doneBtnText: 'Tamamla',
      progressText: '{{current}} / {{total}}',
      overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8,
      stageRadius: 8,
      popoverClass: 'invamed-tour-popover',
      onDestroyStarted: () => {
        localStorage.setItem(`tour_completed_${pageName}`, 'true');
        d.destroy();
      },
      steps: driverSteps
    });
    driverRef.current = d;
    d.drive();
  };

  // Cleanup
  useEffect(() => {
    return () => { if (driverRef.current) try { driverRef.current.destroy(); } catch (e) {} };
  }, []);

  if (!user) return null;

  return (
    <div ref={menuRef} className="fixed bottom-6 right-6 z-[9999]">
      {/* Açılır Menü */}
      {open && (
        <div className="absolute bottom-16 right-0 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-2" style={{ animation: 'floatMenuIn 0.2s ease-out' }}>
          {/* Site Tanıtımı */}
          <button
            onClick={startTour}
            disabled={tourLoading || tourSteps.length === 0}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-blue-50 transition group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 6.75V15m6-6v8.25m.503 3.498 4.875-2.437c.381-.19.622-.58.622-1.006V4.82c0-.836-.88-1.38-1.628-1.006l-3.869 1.934c-.317.159-.69.159-1.006 0L9.503 3.252a1.125 1.125 0 0 0-1.006 0L3.622 5.689C3.24 5.88 3 6.27 3 6.695V19.18c0 .836.88 1.38 1.628 1.006l3.869-1.934c.317-.159.69-.159 1.006 0l4.994 2.497c.317.158.69.158 1.006 0Z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-800">Site Tanıtımı</div>
              <div className="text-[11px] text-gray-400">Sayfayı adım adım keşfedin</div>
            </div>
          </button>

          <div className="h-px bg-gray-100 mx-3"></div>

          {/* Yardım */}
          <button
            onClick={() => { setOpen(false); navigate('/yardim'); }}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-emerald-50 transition group"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center flex-shrink-0 shadow-sm group-hover:shadow-md transition">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
              </svg>
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-800">Yardım</div>
              <div className="text-[11px] text-gray-400">Sıkça sorulan sorular ve destek</div>
            </div>
          </button>
        </div>
      )}

      {/* Ana Buton */}
      <button
        onClick={() => setOpen(o => !o)}
        className={`w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 ${
          open
            ? 'bg-gray-700 rotate-45 shadow-gray-300'
            : 'bg-gradient-to-br from-blue-600 to-indigo-700 shadow-blue-200 hover:shadow-2xl'
        }`}
        title="Yardım ve Site Tanıtımı"
      >
        {open ? (
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        ) : (
          <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z" />
          </svg>
        )}
      </button>

      {/* Animasyon CSS */}
      <style>{`
        @keyframes floatMenuIn {
          from { opacity: 0; transform: translateY(8px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
